import React, { useMemo, useState } from 'react';
import {
  X, Upload, FileSpreadsheet, Loader2, Sparkles, Check, AlertTriangle,
  ChevronDown, ChevronRight, Download, ArrowLeft, ArrowRight,
} from 'lucide-react';
import { api, apiPost } from '../../lib/api';
import { inrCompact } from '../../lib/format';
import { toast } from 'sonner';
import { MaterialPicker } from '../pickers/MaterialPicker';

const TARGET_FIELDS = [
  { key: 'po_no', label: 'PO Number', required: true },
  { key: 'vendor_name', label: 'Vendor Name', required: true },
  { key: 'materials', label: 'Material Description', required: true, hint: 'A single cell may contain multiple materials — AI splits them' },
  { key: 'qty', label: 'Quantity' },
  { key: 'uom', label: 'UOM' },
  { key: 'rate', label: 'Rate' },
  { key: 'amount', label: 'Amount' },
  { key: 'po_date', label: 'PO Date' },
  { key: 'delivery_date', label: 'Delivery Date' },
  { key: 'project_name', label: 'Project' },
  { key: 'terms', label: 'Terms / Remarks' },
];

const STEPS = ['Upload', 'Map Columns', 'Match Materials', 'Confirm', 'Done'];

const VENDOR_TAG = {
  new: { txt: 'New vendor — will be created (incomplete registration)', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  existing_incomplete: { txt: 'Vendor exists — incomplete registration', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  existing_complete: { txt: 'Vendor on file', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export default function POImportWizard({ onClose, onImported }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);     // upload response
  const [mapping, setMapping] = useState({});
  const [pos, setPos] = useState([]);               // parsed POs (client-editable)
  const [summary, setSummary] = useState(null);
  const [statusChoice, setStatusChoice] = useState('Completed');
  const [result, setResult] = useState(null);

  // ── Step 1: upload ─────────────────────────────────────────────────────────
  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f, f.name);
      const { data } = await api.post('/po-import/upload', fd, { timeout: 120000 });
      setSession(data);
      setMapping(data.suggested_mapping || {});
      setStep(1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not read the file');
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/po-import/template', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'po-import-template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Template download failed'); }
  };

  // ── Step 2 → 3: parse + match ──────────────────────────────────────────────
  const runParse = async () => {
    const missing = TARGET_FIELDS.filter((f) => f.required && !mapping[f.key]);
    if (missing.length) return toast.error(`Map: ${missing.map((m) => m.label).join(', ')}`);
    setBusy(true);
    try {
      const data = await apiPost(`/po-import/${session.session_id}/parse`, { mapping, use_ai: true }, { timeout: 180000 });
      setPos(data.pos.map((p) => ({
        ...p,
        excluded: p.existing_po,         // duplicates pre-excluded
        items: p.items.map((it) => ({
          ...it,
          excluded: false,
          chosen_id: it.match_material_id,
          chosen_name: it.match_material_id ? it.candidates?.[0]?.name : '',
        })),
      })));
      setSummary(data.summary);
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Parse failed');
    } finally { setBusy(false); }
  };

  // ── Step 3 helpers ─────────────────────────────────────────────────────────
  const patchItem = (pi, ii, patch) => setPos((arr) => arr.map((p, i) => i !== pi ? p : {
    ...p, items: p.items.map((it, j) => (j !== ii ? it : { ...it, ...patch })),
  }));
  const patchPO = (pi, patch) => setPos((arr) => arr.map((p, i) => (i !== pi ? p : { ...p, ...patch })));

  const included = useMemo(() => pos
    .filter((p) => !p.excluded)
    .map((p) => ({ ...p, items: p.items.filter((it) => !it.excluded) }))
    .filter((p) => p.items.length), [pos]);
  const unresolved = useMemo(
    () => included.reduce((n, p) => n + p.items.filter((it) => !it.chosen_id).length, 0),
    [included],
  );
  const grandTotal = useMemo(
    () => included.reduce((s, p) => s + p.items.reduce((t, it) => t + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0), 0),
    [included],
  );
  const newVendors = useMemo(
    () => [...new Set(included.filter((p) => p.vendor_status === 'new').map((p) => p.vendor_name))],
    [included],
  );

  // ── Step 4: commit ─────────────────────────────────────────────────────────
  const commit = async () => {
    setBusy(true);
    try {
      const payload = {
        status: statusChoice,
        pos: included.map((p) => ({
          po_no: p.po_no, vendor_name: p.vendor_name, project_name: p.project_name || '',
          po_date: p.po_date, delivery_date: p.delivery_date, terms: p.terms || '',
          items: p.items.map((it) => ({
            material_id: it.chosen_id, name: it.name,
            qty: Number(it.qty) || 0, uom: it.uom || '', rate: Number(it.rate) || 0,
          })),
        })),
      };
      const data = await apiPost(`/po-import/${session.session_id}/commit`, payload);
      setResult(data);
      setStep(4);
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm w-full max-w-5xl max-h-[92vh] flex flex-col" data-testid="po-import-wizard">
        {/* Header + stepper */}
        <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between shrink-0">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">Bulk Upload</div>
            <h3 className="font-display font-bold text-xl mt-0.5">Purchase Order Import</h3>
          </div>
          <div className="hidden md:flex items-center gap-1">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1">
                <span className={`px-2 py-1 rounded-sm text-[10px] font-display font-bold uppercase tracking-wider ${i === step ? 'bg-[#1F7A6E] text-white' : i < step ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                  {i < step ? <Check size={10} className="inline mr-0.5" /> : null}{s}
                </span>
                {i < STEPS.length - 1 && <ChevronRight size={12} className="text-slate-300" />}
              </div>
            ))}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="po-import-close"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto flex-1 p-5">
          {/* ── STEP 0: Upload ── */}
          {step === 0 && (
            <div className="max-w-xl mx-auto text-center py-10">
              <FileSpreadsheet size={40} className="mx-auto text-[#1F7A6E] mb-3" />
              <h4 className="font-display font-bold text-lg">Upload your PO sheet</h4>
              <p className="text-sm text-slate-500 mt-1 mb-6">
                Excel (.xlsx) with PO number, vendor, materials, qty &amp; rate. Rows with the same PO number
                become one PO. A single material cell may list several materials — AI will split them.
              </p>
              <label className="block border-2 border-dashed border-slate-300 hover:border-[#1F7A6E] rounded-sm p-10 cursor-pointer">
                <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={onPickFile} data-testid="po-import-file-input" />
                {busy
                  ? <span className="flex items-center justify-center gap-2 text-sm text-teal-700"><Loader2 size={16} className="animate-spin" /> Reading sheet…</span>
                  : <span className="flex items-center justify-center gap-2 text-sm font-display font-bold"><Upload size={16} /> Click to choose the Excel file</span>}
              </label>
              <button onClick={downloadTemplate} className="mt-4 text-xs text-[#1F7A6E] hover:underline flex items-center gap-1 mx-auto" data-testid="po-import-template-btn">
                <Download size={12} /> Download the import template
              </button>
            </div>
          )}

          {/* ── STEP 1: Mapping ── */}
          {step === 1 && session && (
            <div>
              <div className="text-sm text-slate-600 mb-4">
                <strong className="font-display">{session.row_count}</strong> data rows found in <span className="font-mono text-xs">{session.headers.length}</span> columns.
                Match each ERP field to a column from your sheet — we pre-filled our best guesses.
              </div>
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl">
                {TARGET_FIELDS.map((f) => (
                  <div key={f.key} className="flex items-center gap-3">
                    <div className="w-44 shrink-0">
                      <span className="text-xs font-display font-bold">{f.label}{f.required && <span className="text-rose-600"> *</span>}</span>
                      {f.hint && <div className="text-[10px] text-slate-400 leading-tight">{f.hint}</div>}
                    </div>
                    <select
                      value={mapping[f.key] || ''}
                      onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value || null }))}
                      className="flex-1 px-2 py-1.5 border border-slate-300 rounded-sm text-sm bg-white"
                      data-testid={`po-map-${f.key}`}
                    >
                      <option value="">— not in sheet —</option>
                      {session.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              {session.sample_rows?.length > 0 && (
                <div className="mt-6">
                  <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 mb-1">First rows of your sheet</div>
                  <div className="overflow-x-auto border border-slate-200 rounded-sm">
                    <table className="dense-table w-full text-xs">
                      <thead><tr>{session.headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                      <tbody>
                        {session.sample_rows.map((r, i) => (
                          <tr key={i}>{session.headers.map((h) => <td key={h} className="max-w-[220px] truncate">{r[h] != null ? String(r[h]) : ''}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: Match materials ── */}
          {step === 2 && summary && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                <Chip cls="bg-slate-100 text-slate-700">{summary.pos} POs · {summary.items} items</Chip>
                <Chip cls="bg-emerald-50 text-emerald-700">{summary.matched} auto-matched</Chip>
                {summary.review > 0 && <Chip cls="bg-amber-50 text-amber-700">{summary.review} need review</Chip>}
                {summary.unmatched > 0 && <Chip cls="bg-rose-50 text-rose-700">{summary.unmatched} unmatched</Chip>}
                {summary.ai_cells_parsed > 0 && <Chip cls="bg-teal-50 text-teal-700"><Sparkles size={10} className="inline mr-0.5" />{summary.ai_cells_parsed} cells AI-split</Chip>}
                {summary.duplicate_pos?.length > 0 && <Chip cls="bg-rose-50 text-rose-700">{summary.duplicate_pos.length} PO№ already exist (excluded)</Chip>}
              </div>
              {pos.map((p, pi) => (
                <POCard key={p.po_no} po={p} onPatchPO={(patch) => patchPO(pi, patch)} onPatchItem={(ii, patch) => patchItem(pi, ii, patch)} />
              ))}
            </div>
          )}

          {/* ── STEP 3: Confirm ── */}
          {step === 3 && (
            <div className="max-w-2xl mx-auto space-y-5">
              <h4 className="font-display font-bold text-lg">Ready to import</h4>
              <div className="grid grid-cols-3 gap-3 text-center">
                <Stat label="Purchase Orders" value={included.length} />
                <Stat label="Line Items" value={included.reduce((n, p) => n + p.items.length, 0)} />
                <Stat label="Total Value" value={inrCompact(grandTotal)} />
              </div>
              <div>
                <label className="text-xs font-display font-bold block mb-1.5">Import these POs as</label>
                <select value={statusChoice} onChange={(e) => setStatusChoice(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="po-import-status-select">
                  <option value="Completed">Completed — historical / already fulfilled POs</option>
                  <option value="Approved">Approved — live POs, already sanctioned</option>
                  <option value="Pending">Pending — enter the normal approval matrix</option>
                </select>
              </div>
              {newVendors.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 text-xs text-orange-800" data-testid="po-import-new-vendors">
                  <AlertTriangle size={13} className="inline mr-1 -mt-0.5" />
                  <strong>{newVendors.length} new vendor(s)</strong> will be created in Vendor Master with
                  <strong> INCOMPLETE registration</strong> (only the name is known): {newVendors.join(', ')}.
                  New manual POs to them stay blocked until GST / PAN / bank / contact details are completed.
                </div>
              )}
            </div>
          )}

          {/* ── STEP 4: Done ── */}
          {step === 4 && result && (
            <div className="max-w-xl mx-auto text-center py-10" data-testid="po-import-result">
              <Check size={40} className="mx-auto text-emerald-600 mb-3" />
              <h4 className="font-display font-bold text-xl">{result.created} PO{result.created === 1 ? '' : 's'} imported · {inrCompact(result.total_value)}</h4>
              {result.vendors_created?.length > 0 && (
                <p className="text-sm text-slate-600 mt-2">Vendors created (incomplete registration): {result.vendors_created.join(', ')}</p>
              )}
              {result.skipped_duplicates?.length > 0 && (
                <p className="text-sm text-amber-700 mt-1">Skipped (PO№ already exists): {result.skipped_duplicates.join(', ')}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between shrink-0">
          <button
            onClick={() => (step === 0 || step === 4 ? onClose() : setStep((s) => s - 1))}
            className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm font-display font-bold hover:border-slate-400 flex items-center gap-1"
            data-testid="po-import-back"
          >
            {step === 0 || step === 4 ? 'Close' : <><ArrowLeft size={12} /> Back</>}
          </button>
          <div className="flex items-center gap-3">
            {step === 2 && unresolved > 0 && (
              <span className="text-xs text-rose-600 font-display font-bold" data-testid="po-import-unresolved">
                {unresolved} item(s) still need a Material Master match (or exclude them)
              </span>
            )}
            {step === 1 && (
              <div className="flex items-center gap-2">
                {busy && <span className="text-[11px] text-slate-500">AI is splitting material cells — this can take 30–90s on big sheets…</span>}
                <button onClick={runParse} disabled={busy}
                  className="px-4 py-2 text-xs bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm font-display font-bold disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="po-import-parse-btn">
                  {busy ? <><Loader2 size={12} className="animate-spin" /> AI parsing materials…</> : <><Sparkles size={12} /> Parse &amp; Match Materials</>}
                </button>
              </div>
            )}
            {step === 2 && (
              <button onClick={() => setStep(3)} disabled={unresolved > 0 || !included.length}
                className="px-4 py-2 text-xs bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm font-display font-bold disabled:opacity-50 flex items-center gap-1.5"
                data-testid="po-import-continue-btn">
                Continue <ArrowRight size={12} />
              </button>
            )}
            {step === 3 && (
              <button onClick={commit} disabled={busy || !included.length}
                className="px-4 py-2 text-xs bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm font-display font-bold disabled:opacity-50 flex items-center gap-1.5"
                data-testid="po-import-commit-btn">
                {busy ? <><Loader2 size={12} className="animate-spin" /> Importing…</> : <>Import {included.length} PO{included.length === 1 ? '' : 's'}</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Chip = ({ cls, children }) => (
  <span className={`px-2 py-1 rounded-sm font-display font-bold ${cls}`}>{children}</span>
);

const Stat = ({ label, value }) => (
  <div className="border border-slate-200 rounded-sm p-3">
    <div className="font-display font-bold text-xl">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-display">{label}</div>
  </div>
);

function POCard({ po, onPatchPO, onPatchItem }) {
  const [open, setOpen] = useState(true);
  const tag = VENDOR_TAG[po.vendor_status] || VENDOR_TAG.existing_complete;
  return (
    <div className={`border rounded-sm ${po.excluded ? 'border-slate-200 opacity-50' : 'border-slate-300'}`} data-testid={`po-card-${po.po_no}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 cursor-pointer" onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="font-mono text-xs font-bold">{po.po_no}</span>
        <span className="text-sm font-display font-bold truncate">{po.vendor_name || '— no vendor —'}</span>
        <span className={`px-1.5 py-0.5 text-[10px] border rounded-sm font-display ${tag.cls}`}>{tag.txt}</span>
        {po.existing_po && <span className="px-1.5 py-0.5 text-[10px] bg-rose-50 text-rose-700 border border-rose-200 rounded-sm font-display font-bold">PO№ exists — skipped</span>}
        <span className="text-xs text-slate-400 ml-auto">{po.po_date || ''}</span>
        <label className="flex items-center gap-1 text-[10px] font-display font-bold uppercase" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={!po.excluded} disabled={po.existing_po}
            onChange={(e) => onPatchPO({ excluded: !e.target.checked })} data-testid={`po-include-${po.po_no}`} /> include
        </label>
      </div>
      {open && !po.excluded && (
        <table className="w-full dense-table text-xs">
          <thead><tr><th className="w-[30%]">Parsed Material</th><th className="w-16">Qty</th><th className="w-16">UOM</th><th className="w-20">Rate</th><th>Material Master match</th><th className="w-10"></th></tr></thead>
          <tbody>
            {po.items.map((it, ii) => (
              <tr key={it.item_id} className={it.excluded ? 'opacity-40' : ''}>
                <td>
                  <div className="font-semibold">{it.name}</div>
                  {it.raw_text !== it.name && <div className="text-[10px] text-slate-400 truncate max-w-[260px]" title={it.raw_text}>from: {it.raw_text}</div>}
                </td>
                <td><input type="number" value={it.qty} onChange={(e) => onPatchItem(ii, { qty: e.target.value })} className="w-16 px-1 py-0.5 border border-slate-200 rounded-sm" /></td>
                <td><input value={it.uom || ''} onChange={(e) => onPatchItem(ii, { uom: e.target.value })} className="w-14 px-1 py-0.5 border border-slate-200 rounded-sm" /></td>
                <td><input type="number" value={it.rate} onChange={(e) => onPatchItem(ii, { rate: e.target.value })} className="w-20 px-1 py-0.5 border border-slate-200 rounded-sm" /></td>
                <td>
                  {it.chosen_id ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm" data-testid={`po-item-matched-${it.item_id}`}>
                      <Check size={10} /> {it.chosen_name || it.candidates?.find((c) => c.material_id === it.chosen_id)?.name || 'matched'}
                      {it.match_score > 0 && it.chosen_id === it.candidates?.[0]?.material_id && <em className="not-italic text-[9px] text-emerald-600">{Math.round(it.candidates[0].score)}%</em>}
                      <button onClick={() => onPatchItem(ii, { chosen_id: null, chosen_name: '' })} className="hover:text-rose-600"><X size={10} /></button>
                    </span>
                  ) : (
                    <div className="space-y-1">
                      {it.candidates?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {it.candidates.map((c) => (
                            <button key={c.material_id} onClick={() => onPatchItem(ii, { chosen_id: c.material_id, chosen_name: c.name })}
                              className="px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 rounded-sm hover:border-amber-500"
                              data-testid={`po-item-candidate-${it.item_id}`}>
                              {c.name} <em className="not-italic text-[9px]">{Math.round(c.score)}%</em>
                            </button>
                          ))}
                        </div>
                      )}
                      <MaterialPicker compact testid={`po-item-picker-${it.item_id}`}
                        placeholder={it.candidates?.length ? 'or search / add new…' : 'Search Material Master or add new…'}
                        value={null}
                        onSelect={(m) => m && onPatchItem(ii, { chosen_id: m.id, chosen_name: m.name, uom: it.uom || m.uom })} />
                    </div>
                  )}
                </td>
                <td>
                  <button onClick={() => onPatchItem(ii, { excluded: !it.excluded })}
                    title={it.excluded ? 'Re-include' : 'Exclude this line'}
                    className="text-slate-400 hover:text-rose-600" data-testid={`po-item-exclude-${it.item_id}`}>
                    <X size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
